import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { FeatureFlagsProvider } from "@/context/FeatureFlagsContext";
import FeatureFlag, { FeatureFlagRedirect } from "@/components/FeatureFlag";
import { Navigate as _Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import OrientationGate from "@/components/OrientationGate";
import Login from "@/pages/Login";
import Maintenance from "@/pages/Maintenance";
import Dashboard from "@/pages/Dashboard";
import Strutture from "@/pages/Strutture";
import StrutturaDettaglio from "@/pages/StrutturaDettaglio";
import Utenti from "@/pages/Utenti";
import Profilo from "@/pages/Profilo";
import Guida from "@/pages/Guida";

function RedirectAuditId() {
  const { id } = useParams();
  return <Navigate to={`/ispezioni/${id}`} replace />;
}
import Controlli from "@/pages/Controlli";
import ControlloDettaglio from "@/pages/ControlloDettaglio";
import Templates from "@/pages/Templates";
import Audit from "@/pages/Audit";
import AuditDettaglio from "@/pages/AuditDettaglio";
import Performance from "@/pages/Performance";
import Housekeeping from "@/pages/Housekeeping";
import Staff from "@/pages/Staff";
import StaffDettaglio from "@/pages/StaffDettaglio";
import NuovaValutazione from "@/pages/NuovaValutazione";
import ValutazioneDettaglio from "@/pages/ValutazioneDettaglio";
import ConfigurazioneValutazioni from "@/pages/ConfigurazioneValutazioni";
import Relazioni from "@/pages/Relazioni";
import { RelazioniMensiliList as RelazioniV2List, RelazioneEditor as RelazioneV2Editor } from "@/pages/RelazioniMensiliV2";
import ConfigurazioneObiettiviHotel from "@/pages/ConfigurazioneObiettiviHotel";
import ConfigurazioneSchemaRelazioni from "@/pages/ConfigurazioneSchemaRelazioni";
import RelazioneEditor from "@/pages/RelazioneEditor";
import Alert from "@/pages/Alert";
import ConfigurazioneAlert from "@/pages/ConfigurazioneAlert";
import Notifiche from "@/pages/Notifiche";
import Report from "@/pages/Report";
import ReportSettimanali from "@/pages/ReportSettimanali";
import AuditLog from "@/pages/AuditLog";
import ConfigurazioneAI from "@/pages/ConfigurazioneAI";
import SchedulerStatus from "@/pages/SchedulerStatus";
import ConfigurazioneChecklistCamera from "@/pages/ConfigurazioneChecklistCamera";
import ControlloCameraDettaglio from "@/pages/ControlloCameraDettaglio";
import DemoTools from "@/pages/DemoTools";
import Installa from "@/pages/Installa";
import WebReputation from "@/pages/WebReputation";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import AdminHealth from "@/pages/AdminHealth";
import PwaInstallBanner from "@/components/PwaInstallBanner";

export default function App() {
  // Portrait-only lock — v106. Silently try to lock orientation; Safari rejects,
  // but on Android/Chrome PWA it works. The <OrientationGate /> overlay is the fallback.
  useEffect(() => {
    try {
      if (typeof screen !== "undefined" && screen.orientation && typeof screen.orientation.lock === "function") {
        const p = screen.orientation.lock("portrait");
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } catch (_) { /* noop */ }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureFlagsProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="strutture" element={<Strutture />} />
              <Route path="strutture/:id" element={<StrutturaDettaglio />} />
              <Route
                path="utenti"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <Utenti />
                  </ProtectedRoute>
                }
              />
              <Route path="controlli" element={<Controlli />} />
              <Route path="controlli/:id" element={<ControlloDettaglio />} />
              <Route path="ispezioni" element={<Audit />} />
              <Route path="ispezioni/:id" element={<AuditDettaglio />} />
              <Route path="audit" element={<Navigate to="/ispezioni" replace />} />
              <Route path="audit/:id" element={<RedirectAuditId />} />
              <Route
                path="performance"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "resident_manager"]}>
                    <Performance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="housekeeping"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "housekeeping_manager", "resident_manager"]}>
                    <Housekeeping />
                  </ProtectedRoute>
                }
              />
              <Route path="staff" element={<Staff />} />
              <Route path="staff/:id" element={<StaffDettaglio />} />
              <Route path="staff/:id/nuova-valutazione" element={<NuovaValutazione />} />
              <Route path="staff/:id/valutazione/:reviewId" element={<ValutazioneDettaglio />} />
              <Route
                path="configurazione/valutazioni"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <ConfigurazioneValutazioni />
                  </ProtectedRoute>
                }
              />
              <Route
                path="configurazione/alert"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <ConfigurazioneAlert />
                  </ProtectedRoute>
                }
              />
              <Route
                path="relazioni"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "resident_manager"]}>
                    <RelazioniV2List />
                  </ProtectedRoute>
                }
              />
              <Route
                path="relazioni-legacy"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "resident_manager"]}>
                    <Relazioni />
                  </ProtectedRoute>
                }
              />
              <Route
                path="relazioni/:id"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "resident_manager"]}>
                    <RelazioneV2Editor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="relazioni-legacy/:id"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area", "resident_manager"]}>
                    <RelazioneEditor />
                  </ProtectedRoute>
                }
              />
              <Route path="configurazione/obiettivi-hotel" element={<ProtectedRoute roles={["super_admin"]}><ConfigurazioneObiettiviHotel /></ProtectedRoute>} />
              <Route path="configurazione/schema-relazioni" element={<ProtectedRoute roles={["super_admin"]}><ConfigurazioneSchemaRelazioni /></ProtectedRoute>} />
              <Route path="alert" element={<Alert />} />
              <Route path="notifiche" element={<Notifiche />} />
              <Route path="report" element={<Report />} />
              <Route path="configurazione/audit-log" element={<ProtectedRoute roles={["super_admin"]}><AuditLog /></ProtectedRoute>} />
              <Route path="configurazione/ai" element={<ProtectedRoute roles={["super_admin"]}><ConfigurazioneAI /></ProtectedRoute>} />
              <Route path="configurazione/scheduler" element={<ProtectedRoute roles={["super_admin"]}><SchedulerStatus /></ProtectedRoute>} />
              <Route path="admin/health" element={<ProtectedRoute roles={["super_admin"]}><AdminHealth /></ProtectedRoute>} />
              <Route path="configurazione/checklist-camera" element={<ProtectedRoute roles={["super_admin"]}><ConfigurazioneChecklistCamera /></ProtectedRoute>} />
              <Route path="controlli/:id/camere/:roomId" element={<ControlloCameraDettaglio />} />
              <Route
                path="templates"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <Templates />
                  </ProtectedRoute>
                }
              />
              <Route path="profilo" element={<Profilo />} />
              <Route path="guida" element={<Guida />} />
              <Route path="installa" element={<Installa />} />
              <Route path="performance/web-reputation" element={<WebReputation />} />
              <Route
                path="performance/report-settimanali"
                element={
                  <ProtectedRoute roles={["super_admin", "capo_area"]}>
                    <FeatureFlag
                      flag="report_settimanali"
                      fallback={<FeatureFlagRedirect message="La sezione Report Settimanali non è ancora abilitata per il tuo utente." />}
                    >
                      <ReportSettimanali />
                    </FeatureFlag>
                  </ProtectedRoute>
                }
              />
              <Route
                path="configurazione/feature-flags"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <AdminFeatureFlags />
                  </ProtectedRoute>
                }
              />
              <Route
                path="configurazione/demo-tools"
                element={
                  <ProtectedRoute roles={["super_admin"]}>
                    <DemoTools />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PwaInstallBanner />
        </BrowserRouter>
        <OrientationGate />
        <Toaster position="top-right" richColors closeButton />
        </FeatureFlagsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
